import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { initLiveUpdates } from "@/lib/liveUpdates";
import { initKeyboardBehavior } from "@/lib/keyboard";
import { initStatusBar } from "@/lib/statusBar";
import { hydrateCacheFromDisk } from "@/lib/api";
import { pendingInviteUrl } from "@/lib/pendingInvite";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import { EntitlementsProvider } from "@/context/EntitlementsContext";
import { PaywallProvider } from "@/context/PaywallContext";
import { GlobalPaywallSheet } from "@/components/PaywallSheet";
import { PaywallBridge } from "@/components/PaywallBridge";
import { DowngradeNoticeSheet } from "@/components/DowngradeNoticeSheet";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider, applyPersistedThemeEarly } from "@/context/ThemeContext";
import { AppShell } from "@/components/AppShell";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { useCapacitorDeepLinks } from "@/lib/useCapacitorDeepLinks";

function DeepLinkBridge() {
  useCapacitorDeepLinks();
  return null;
}

// Apply persisted theme synchronously at module load — happens BEFORE
// React renders, so users with dark mode saved never see a one-frame
// light flash on cold start.
applyPersistedThemeEarly();

// Critical-path pages — eager (cold-start hot path)
import SignIn from "@/pages/Onboarding/SignIn";
import Landing from "@/pages/Landing";
import Today from "@/pages/Today";
import Plan from "@/pages/Plan";

// Code-split the rest. Splash shows while chunks load (~100-200ms once
// network is warm). Massive impact on initial bundle parse time —
// removes Recipes, Admin, Remember, Talk, You, all detail screens,
// Onboarding, and Legal from the first paint critical path.
const FamilyName = lazy(() => import("@/pages/Onboarding/FamilyName"));
const FirstMember = lazy(() => import("@/pages/Onboarding/FirstMember"));
const InviteStep = lazy(() => import("@/pages/Onboarding/InviteStep"));
const PlanSelect = lazy(() => import("@/pages/Onboarding/PlanSelect"));
const Pricing = lazy(() => import("@/pages/marketing/Pricing"));
const Features = lazy(() => import("@/pages/marketing/Features"));
const InviteAccept = lazy(() => import("@/pages/InviteAccept"));
const VerifyEmail = lazy(() => import("@/pages/Onboarding/VerifyEmail"));
const EmailInviteAccept = lazy(() => import("@/pages/EmailInviteAccept"));
const EmailInviteDecline = lazy(() => import("@/pages/EmailInviteDecline"));
const AuthCallback = lazy(() => import("@/pages/Onboarding/AuthCallback"));
const Talk = lazy(() => import("@/pages/Talk"));
const Remember = lazy(() => import("@/pages/Remember"));
const Activities = lazy(() => import("@/pages/Activities"));
const Study = lazy(() => import("@/pages/Study"));
const You = lazy(() => import("@/pages/You"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const ProfileSetup = lazy(() => import("@/pages/ProfileSetup"));
const Recipes = lazy(() => import("@/pages/Recipes"));
const PrivacyPolicy = lazy(() => import("@/pages/Legal").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("@/pages/Legal").then(m => ({ default: m.TermsOfService })));
const DeleteAccount = lazy(() => import("@/pages/Legal").then(m => ({ default: m.DeleteAccount })));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const ActivityDetail = lazy(() => import("@/pages/ActivityDetail"));
const Locator = lazy(() => import("@/pages/Locator"));
const ChoreDetail = lazy(() => import("@/pages/ChoreDetail"));
const PollDetail = lazy(() => import("@/pages/PollDetail"));
const AdminApp = lazy(() => import("@/admin/AdminApp"));

function Splash() {
  return (
    <div className="kinly-canvas flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-primary mx-auto mb-4 orb-pulse" />
        <p className="text-muted-foreground text-sm">Waking Kinly up…</p>
      </div>
    </div>
  );
}

// One shared QueryClient. staleTime keeps screens INSTANT when navigating
// between tabs — data is still considered fresh for 60s. keepPreviousData
// stops the "blank flash" when switching surfaces.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

function RootGate() {
  const { user, family, pendingInvites } = useAuth();
  if (user === undefined || family === undefined) return <Splash />;
  if (user === null) return <Landing />;
  // Backend-driven invite check — wins over the sessionStorage hint because
  // it survives device hops (open email on phone, install TestFlight, sign in there).
  if (pendingInvites && pendingInvites.length > 0) {
    return <Navigate to={`/i/accept/${pendingInvites[0].accept_token}`} replace />;
  }
  // Legacy sessionStorage hint (in case the email click happened in the same
  // browser tab as signup — we want to keep that fast-path working too).
  const inviteUrl = pendingInviteUrl();
  if (inviteUrl) return <Navigate to={inviteUrl} replace />;
  if (!family) return <Navigate to="/onboarding/family-name" replace />;
  return <Navigate to="/today" replace />;
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Splash />;
  if (user === null) return <Navigate to="/" replace />;
  return children;
}

function RequireFamily({ children }) {
  const { user, family, pendingInvites } = useAuth();
  if (user === undefined || family === undefined) return <Splash />;
  if (user === null) return <Navigate to="/" replace />;
  if (!family) {
    // Backend invites trump everything — never auto-create a family for a
    // user whose email already belongs to one. This is the device-agnostic fix.
    if (pendingInvites && pendingInvites.length > 0) {
      return <Navigate to={`/i/accept/${pendingInvites[0].accept_token}`} replace />;
    }
    const inviteUrl = pendingInviteUrl();
    if (inviteUrl) return <Navigate to={inviteUrl} replace />;
    return <Navigate to="/onboarding/family-name" replace />;
  }
  return children;
}

export default function App() {
  // Hydrate IndexedDB cache, but DO NOT block the initial paint.
  // On iOS Capacitor WKWebView, IDB can occasionally hang for tens of
  // seconds on first launch (storage perms / DB upgrade). We race the
  // hydration against a 600ms ceiling so the app ALWAYS paints fast.
  // If hydration finishes after the timeout, the cached responses
  // simply become available on the next tab navigation — no harm done.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let done = false;
    const finish = () => { if (!done) { done = true; setHydrated(true); } };
    // Hard ceiling: 600ms. Beats the user's perception threshold.
    const t = setTimeout(finish, 600);
    hydrateCacheFromDisk()
      .catch(() => {})
      .finally(() => { clearTimeout(t); finish(); });
    // Each init is wrapped defensively — a native bridge hiccup in any
    // one MUST NOT crash the app. App Store reviewers run on devices we
    // don't get to debug; better to silently degrade a feature than to
    // hit Guideline 2.1(a) "crashed on launch" rejection.
    try { initLiveUpdates(); } catch (e) { console.warn("[Kinly] liveUpdates init threw", e); }
    try { initKeyboardBehavior(); } catch (e) { console.warn("[Kinly] keyboard init threw", e); }
    try { initStatusBar(); } catch (e) { console.warn("[Kinly] statusBar init threw", e); }
  }, []);

  if (!hydrated) {
    return (
      <div className="kinly-canvas flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-primary orb-pulse" />
      </div>
    );
  }

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <SubscriptionProvider>
              <EntitlementsProvider>
                <PaywallProvider>
                  <PaywallBridge />
              <BrowserRouter>
                <DeepLinkBridge />
                <Suspense fallback={<Splash />}>
        <Routes>
          {/* Public landing decides where to go */}
          <Route path="/" element={<RootGate />} />
          <Route path="/signin" element={<SignIn defaultMode="login" />} />
          <Route path="/signup" element={<SignIn defaultMode="signup" />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/choose-plan" element={<PlanSelect />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/features" element={<Features />} />

          {/* Onboarding (auth required) */}
          <Route
            path="/onboarding/verify-email"
            element={
              <RequireAuth>
                <VerifyEmail />
              </RequireAuth>
            }
          />
          <Route
            path="/onboarding/family-name"
            element={
              <RequireAuth>
                <FamilyName />
              </RequireAuth>
            }
          />
          <Route
            path="/onboarding/first-member"
            element={
              <RequireAuth>
                <FirstMember />
              </RequireAuth>
            }
          />
          <Route
            path="/onboarding/invite"
            element={
              <RequireFamily>
                <InviteStep />
              </RequireFamily>
            }
          />

          {/* Email-bound invite landing — new hardened flow (must come BEFORE /i/:token) */}
          <Route path="/i/accept/:token" element={<EmailInviteAccept />} />
          <Route path="/i/decline/:token" element={<EmailInviteDecline />} />

          {/* Legacy invite landing — public preview, becomes joinable post-auth */}
          <Route path="/i/:token" element={<InviteAccept />} />

          {/* Public legal pages — required by Apple App Store + Google Play */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/delete-data" element={<DeleteAccount />} />

          {/* Main surfaces inside shell */}
          <Route
            element={
              <RequireFamily>
                <AppShell />
              </RequireFamily>
            }
          >
            <Route path="/today" element={<Today />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/plan/calendar" element={<Plan />} />
            <Route path="/plan/chores" element={<Plan />} />
            <Route path="/plan/polls" element={<Plan />} />
            <Route path="/talk" element={<Talk />} />
            <Route path="/remember" element={<Remember />} />
            <Route path="/memories" element={<Remember />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/study" element={<Study />} />
            <Route path="/you" element={<You />} />
            <Route path="/you/subscription" element={<Subscription />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/you/profile" element={<ProfileSetup />} />
            <Route path="/you/profile/:personId" element={<ProfileSetup />} />
            <Route path="/recipes" element={<Recipes />} />
            <Route path="/recipes/:recipeId" element={<Recipes />} />
            <Route path="/whereabouts" element={<Locator />} />
          </Route>

          {/* Detail pages (their own canvas; no tab bar) */}
          <Route path="/event/:eventId" element={<RequireFamily><EventDetail /></RequireFamily>} />
          <Route path="/activity/:activityId/event/:eventId" element={<RequireFamily><ActivityDetail /></RequireFamily>} />
          <Route path="/locator" element={<Navigate to="/whereabouts" replace />} />
          <Route path="/chore/:choreId" element={<RequireFamily><ChoreDetail /></RequireFamily>} />
          <Route path="/poll/:pollId" element={<RequireFamily><PollDetail /></RequireFamily>} />

          {/* Internal — Geminii staff admin console (separate identity store + MFA). */}
          <Route path="/admin/*" element={<AdminApp />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
            </Suspense>
      </BrowserRouter>
                  <GlobalPaywallSheet />
                  <DowngradeNoticeSheet />
                  <Toaster position="top-center" richColors />
                </PaywallProvider>
              </EntitlementsProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </AppErrorBoundary>
  );
}
